import { create } from 'zustand'

// ---------------------------------------------------------------------------
// How the Workbench opens an object's Edit details and its designer:
//
//   tab        → in place, in the right panel (the tree stays usable)
//   modal      → a large dialog over the page
//   fullscreen → a window covering the whole screen
//
// Each has its own choice, picked from the switcher on the view's bar and
// remembered in this browser (like the theme and the Ask-AI dock), so the
// next object opens the same way.
// ---------------------------------------------------------------------------

export type ObjectViewMode = 'tab' | 'modal' | 'fullscreen'
export type ObjectViewKind = 'details' | 'designer'

export const OBJECT_VIEW_MODES: ObjectViewMode[] = ['tab', 'modal', 'fullscreen']

const STORAGE_KEY = 'twasta_workbench_object_view_modes'
const DEFAULT_MODES: Record<ObjectViewKind, ObjectViewMode> = {
  details: 'fullscreen',
  designer: 'fullscreen',
}

function savedModes(): Record<ObjectViewKind, ObjectViewMode> {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as Partial<Record<ObjectViewKind, string>>
    const pick = (k: ObjectViewKind): ObjectViewMode =>
      OBJECT_VIEW_MODES.includes(raw[k] as ObjectViewMode) ? raw[k] as ObjectViewMode : DEFAULT_MODES[k]
    return { details: pick('details'), designer: pick('designer') }
  } catch {
    // Storage blocked or a malformed value — fall back to the defaults.
    return { ...DEFAULT_MODES }
  }
}

interface ObjectViewState {
  modes: Record<ObjectViewKind, ObjectViewMode>
  setMode: (kind: ObjectViewKind, mode: ObjectViewMode) => void
}

export const useObjectViewStore = create<ObjectViewState>((set, get) => ({
  modes: savedModes(),
  setMode: (kind, mode) => {
    const modes = { ...get().modes, [kind]: mode }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(modes))
    } catch (err) {
      // The choice still applies for this session; it just won't be remembered.
      console.warn('[objectViewStore] could not save the view preference', err)
    }
    set({ modes })
  },
}))
